// Session-scoped, per-conversation draft store for the work-order chat composer
// (ORI-734). Staged attachments and typed text live only in component state
// (`WorkOrderChatDetails`), so tearing the composer down — a side-panel close,
// Android Back, or a section switch — unmounts the subtree and silently
// discards an unsent selection. This module holds that selection OUTSIDE the
// React tree, keyed by conversation, so a re-open restores it.
//
// Deliberately a module-level Map, not Redux and not Context:
//   - a persisted draft carries a live `File` handle (non-serialisable), so a
//     Redux slice would need per-UUID serializable-check ignores and there is
//     no logout reducer to clear it;
//   - the composer is unmounted by the very event we survive, so a Context
//     provider would have to wrap every host at app level and re-render on each
//     keystroke.
// Same shape + test-reset convention as `inFlightDedupe.ts` /
// `workTypeDiagnostics.ts` — a dependency-free leaf, so auth teardown can
// import its clear without an import cycle.
//
// Session-scoped by construction: a `File` reference cannot survive a full
// page reload (not structured-clone-persistable to storage), so the Map — and
// every draft — is gone on reload. That is intended (ORI-734 AC-8), not a gap.

import type { ChatAttachmentItem } from 'src/views/ServiceRequest/SidePanel/Chat/ImagePreviewContainer';

export interface ChatDraft {
  // Only `status: 'ready'` items are ever stored — a `converting` item's
  // conversion is aborted when the composer goes away (on unmount by
  // `useVideoConversion`, and on an in-place conversation swap by the caller),
  // so it cannot be resumed and must not be persisted. `objectUrl` is omitted:
  // ready tiles carry none, and `ImageThumbnail` mints/revokes its own from `file`.
  attachments: ChatAttachmentItem[];
  messageText: string;
}

const isPendingConversationId = (id: string): boolean => id.startsWith('new-');

/**
 * The draft-store key for a conversation, and the single source of truth for it
 * (both `useChatDraft` and the composer's send handler derive keys through here).
 *
 * A PENDING conversation (`new-*`, no real id yet) MUST be scoped by its owning
 * work order: without it, two work orders' brand-new chats of the same type both
 * key on the bare `new-vendor` literal and one's staged files restore into the
 * other (ORI-734 cross-conversation isolation). A conversation with a real id
 * keys on that id directly (work-order scope is irrelevant — ids are globally
 * unique). Returns `null` when the conversation cannot be safely keyed — a
 * pending id with no work-order scope, or an empty id — so the caller persists
 * and restores nothing rather than risk a collision.
 */
export function chatDraftKey(
  conversationId: string,
  actualConversationId: string | null,
  workOrderUuid?: string | null,
): string | null {
  if (isPendingConversationId(conversationId) && !actualConversationId) {
    return workOrderUuid ? `${workOrderUuid}:${conversationId}` : null;
  }
  const realId = actualConversationId ?? conversationId;
  return realId || null;
}

const drafts = new Map<string, ChatDraft>();

const isEmpty = (draft: ChatDraft): boolean =>
  draft.attachments.length === 0 && draft.messageText.trim() === '';

export function getChatDraft(key: string): ChatDraft | undefined {
  return drafts.get(key);
}

// Prunes an empty draft rather than storing it, so a cleared composer never
// leaves a bucket to restore and the store never accumulates dead entries.
export function setChatDraft(key: string, draft: ChatDraft): void {
  if (isEmpty(draft)) {
    drafts.delete(key);
    return;
  }
  drafts.set(key, draft);
}

export function clearChatDraft(key: string): void {
  drafts.delete(key);
}

// A pending conversation (`new-*`) that gains a real UUID is the SAME
// conversation, not a switch — carry its draft across to the real key.
export function migrateChatDraft(from: string, to: string): void {
  if (from === to) return;
  const draft = drafts.get(from);
  drafts.delete(from);
  if (draft && !isEmpty(draft)) drafts.set(to, draft);
}

// Auth teardown: sign-out, same-tab account switch, and the 401 forced
// sign-out all clear every draft, so an incoming account never inherits the
// outgoing user's staged files.
export function clearAllChatDrafts(): void {
  drafts.clear();
}

// Test-only helpers — do not call from app code.
export function _resetChatDraftsForTests(): void {
  drafts.clear();
}

export function _getAllChatDraftsForTests(): Map<string, ChatDraft> {
  return drafts;
}
